import { Controller } from '@hotwired/stimulus'
import parsePhoneNumber from 'libphonenumber-js/max'
import { getAddress } from '../api/common/search_address'

export default class extends Controller {
  static targets = [
    'postcode',
    'postcodeError',
    'prefectures',
    'cities',
    'towns',
    'occupantCurrentStatus',
    'currentStatusHome',
    'occupantCurrentStatusNote',
    'occupantCurrentStatusDischargeText',
    'assetTypes',
    'currentStatusFacility',
    'facilityStatusLabel',
    'moveout',
    'symptomOfDementias',
    'selfPropelled',
    'transferToBed',
    'dietaryRestrictionDetails',
    'necessaryMedicalTreatments',
    'gastrosvenorFrequency',
    'sputumSuctionFrequency',
    'nightlySputumSuction',
    'oxygenSupply',
    'insulinInjectionYourself',
    'insulinInjectionCare',
    'contractWithDialysisClinic',
    'demandPlaceholder',
    'email',
    'suggest',
    'telInput',
    'telError',
    'telNext',
    'submit',
  ]

  static values = {
    abCategory: String,
  }

  get className () {
    return this.element.dataset.classname
  }

  get isOfferInquiry () {
    return this.element.dataset.isofferinquiry
  }

  updateTowns (towns) {
    this.townsTarget.innerHTML =
      '<option value="">町名を選択してください</option>' +
      towns.map((town) => (
        `<option value="${town.id}">${town.name}</option>`
      )).join()
  }

  updateCities (cities) {
    this.citiesTarget.innerHTML =
      '<option value="">市区町村を選択してください</option>' +
      cities.map((city) => (
        `<option value="${city.id}">${city.name}</option>`
      )).join()
  }

  changePrefecture () {
    if (this.prefectureId) {
      fetch(`/area/prefectures/${this.prefectureId}/cities.json`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
          return response.json()
        })
        .then(cities => this.updateCities(cities))
        .catch(error => {
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(error, { tags: { component: 'inquiry-consultation', operation: 'fetch-cities' } })
          }
          console.error('Failed to fetch cities:', error)
        })
    }
    this.citiesTarget.value = ''
    this.townsTarget.value = ''
  }

  fillAddresses () {
    const param = { post_code: this.postcodeTarget.value }

    getAddress(param).then((address) => {
      if (!address.result.prefecture) {
        this.postcodeErrorTarget.style.display = 'flex'
      } else {
        this.postcodeErrorTarget.style.display = 'none'
        this.prefecturesTarget.value = address.result.prefecture.id
        this.fillCities(address.result.city)
        this.fillTowns(address.result.town)
      }
    })
  }

  async fillCities (selectedCity) {
    try {
      const response = await fetch(`/area/prefectures/${selectedCity.prefecture_id}/cities.json`)
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
      const json = await response.json()
      this.updateCities(json)
      this.citiesTarget.value = selectedCity.id
    } catch (error) {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, { tags: { component: 'inquiry-consultation', operation: 'fill-cities' } })
      }
      console.error('Failed to fetch cities:', error)
    }
  }

  async fillTowns (selectedTown) {
    try {
      const response = await fetch(`/area/cities/${selectedTown.city_id}/towns`)
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
      const json = await response.json()
      this.updateTowns(json)
      this.townsTarget.value = selectedTown.id
    } catch (error) {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, { tags: { component: 'inquiry-consultation', operation: 'fill-towns' } })
      }
      console.error('Failed to fetch towns:', error)
    }
  }

  changeCity () {
    if (this.cityId) {
      fetch(`/area/cities/${this.cityId}/towns`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
          return response.json()
        })
        .then(towns => this.updateTowns(towns))
        .catch(error => {
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(error, { tags: { component: 'inquiry-consultation', operation: 'fetch-towns' } })
          }
          console.error('Failed to fetch towns:', error)
        })
    }
    this.townsTarget.value = ''
  }

  changeCurrentStatusSelectBox (e) {
    const resetElements = document.getElementsByName('inquiry[occupant_current_status_home_id]')
    for (let i = 0; i < resetElements.length; i++) {
      resetElements[i].checked = false
    }
    this.currentStatusHomeTarget.style.display = 'block'
    const inputs = this.currentStatusHomeTarget.querySelectorAll('input[type="radio"]')
    inputs.forEach(input => {
      input.disabled = false
    })

    this.occupantCurrentStatusNoteTarget.style.display = 'block'
  }

  changeCurrentStatus (e) {
    const setHome = (isVisible) => {
      const resetElements = document.getElementsByName('inquiry[occupant_current_status_home_id]')
      for (let i = 0; i < resetElements.length; i++) {
        resetElements[i].checked = false
      }
      this.currentStatusHomeTarget.style.display = isVisible ? 'block' : 'none'
      const inputs = this.currentStatusHomeTarget.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
        input.disabled = !isVisible
      })
    }
    const setMove = (isVisible) => {
      this.moveoutTarget.style.display = isVisible ? 'block' : 'none'
      this.moveoutTarget.querySelector('select').disabled = !isVisible
      this.moveoutTarget.querySelector('select').value = ''
    }
    const setFacility = (isVisible) => {
      this.currentStatusFacilityTarget.style.display = isVisible ? 'block' : 'none'
      const inputs3 = this.currentStatusFacilityTarget.querySelectorAll('input[type="radio"]')
      inputs3.forEach(input => {
        input.disabled = !isVisible
      })
    }

    if (e.target.value === '4') {
      setHome(true)
      setMove(false)
      setFacility(false)
      this.occupantCurrentStatusNoteTarget.style.display = 'none'
    }

    if (e.target.value === '1') {
      setHome(false)
      setMove(true)
      setFacility(false)
      this.occupantCurrentStatusDischargeTextTarget.innerHTML = '退院'
    }

    if (e.target.value === '') {
      setHome(false)
      setMove(false)
      setFacility(true)
      this.occupantCurrentStatusDischargeTextTarget.innerHTML = '退去'
    }

    if (e.target.value === '2' || e.target.value === '3') {
      setMove(true)
    }

    // 現在の居場所で施設系（施設=''／老健='2'／老健以外='3'）を選択中は「施設」ラベルの選択色を維持する。
    // サブ質問「施設は老健でしょうか？」は施設ラジオと同一nameを共有するため、選択でcheckedが外れても見た目を保つ。
    // CSSはViewComponentで名前空間スコープされるため、素のクラスではなく名前空間付き（this.className::）で付与する。
    if (this.hasFacilityStatusLabelTarget) {
      const facilitySelected = (e.target.value === '' || e.target.value === '2' || e.target.value === '3')
      if (facilitySelected) {
        this.facilityStatusLabelTarget.classList.add(`${this.className}::is-facility-selected`)
      } else {
        this.facilityStatusLabelTarget.classList.remove(`${this.className}::is-facility-selected`)
      }
    }
  }

  changeAssetTypes (e) {
    if (this.assetTypesIds.length > 0) {
      this.assetTypesTargets.forEach(element => (element.required = false))
    } else {
      this.assetTypesTargets.forEach(element => (element.required = true))
    }
  }

  changeDementia (e) {
    if (e.target.value === '1') {
      this.symptomOfDementiasTarget.style.display = 'block'
      this.symptomOfDementiasTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.symptomOfDementiasTarget.style.display = 'none'
      this.symptomOfDementiasTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }
  }

  changeMoveSupport (e) {
    const moveSupportQuestionsDisplayedValues = ['00', '11', '22', '33']
    if (e.target.value === '1' || moveSupportQuestionsDisplayedValues.includes(e.target.value)) {
      this.selfPropelledTarget.style.display = 'block'
    } else {
      this.selfPropelledTarget.style.display = 'none'
    }

    if (moveSupportQuestionsDisplayedValues.includes(e.target.value)) {
      this.transferToBedTarget.style.display = 'block'
    } else {
      this.transferToBedTarget.style.display = 'none'
    }
  }

  changeDietaryRestrictions (e) {
    if (e.target.value === 'yes') {
      this.dietaryRestrictionDetailsTarget.style.display = 'block'
    } else {
      this.dietaryRestrictionDetailsTarget.style.display = 'none'
    }
  }

  changeNecessaryMedicalTreatment () {
    if (this.necessaryMedicalTreatmentIds.length > 0) {
      this.necessaryMedicalTreatmentsTargets.forEach(element => (element.required = false))
    } else {
      this.necessaryMedicalTreatmentsTargets.forEach(element => (element.required = true))
    }

    if (this.necessaryMedicalTreatmentIds.includes('1')) {
      this.gastrosvenorFrequencyTarget.style.display = 'block'
      this.gastrosvenorFrequencyTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.gastrosvenorFrequencyTarget.style.display = 'none'
      this.gastrosvenorFrequencyTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }

    if (this.necessaryMedicalTreatmentIds.includes('5')) {
      this.sputumSuctionFrequencyTarget.style.display = 'block'
      this.nightlySputumSuctionTarget.style.display = 'block'
      this.sputumSuctionFrequencyTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
      this.nightlySputumSuctionTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.sputumSuctionFrequencyTarget.style.display = 'none'
      this.nightlySputumSuctionTarget.style.display = 'none'
      this.sputumSuctionFrequencyTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
      this.nightlySputumSuctionTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }

    if (this.necessaryMedicalTreatmentIds.includes('7')) {
      this.oxygenSupplyTarget.style.display = 'block'
      this.oxygenSupplyTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.oxygenSupplyTarget.style.display = 'none'
      this.oxygenSupplyTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }

    if (this.necessaryMedicalTreatmentIds.includes('8')) {
      this.insulinInjectionYourselfTarget.style.display = 'block'
      this.insulinInjectionYourselfTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.insulinInjectionYourselfTarget.style.display = 'none'
      this.insulinInjectionYourselfTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }

    if (this.necessaryMedicalTreatmentIds.includes('9')) {
      this.contractWithDialysisClinicTarget.style.display = 'block'
      this.contractWithDialysisClinicTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.contractWithDialysisClinicTarget.style.display = 'none'
      this.contractWithDialysisClinicTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }
  }

  changeInjectionYourself (e) {
    if (e.target.value === '2') {
      this.insulinInjectionCareTarget.style.display = 'block'
      this.insulinInjectionCareTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.insulinInjectionCareTarget.style.display = 'none'
      this.insulinInjectionCareTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }
  }

  changeDemand (e) {
    if (this.hasDemandPlaceholderTarget) {
      if (e.target.value.length === 0) {
        e.target.previousElementSibling.style.display = 'block'
      } else {
        e.target.previousElementSibling.style.display = 'none'
      }
    }
  }

  changeOccupantTiming (e) {
    if (this.isSendSmsInquiry(e.target.value)) {
      // emailの必須タグを非表示
      document.getElementsByClassName(`${this.className}::email-required`)[0].style.display = 'none'

      // emailのinputのrequiredを削除
      document.querySelector('input[name="inquiry[email]"]').removeAttribute('required')
    } else {
      // emailの必須タグを表示
      document.getElementsByClassName(`${this.className}::email-required`)[0].style.display = 'inline-block'

      // emailのinputにrequiredを追加
      document.querySelector('input[name="inquiry[email]"]').setAttribute('required', '')
    }
  }

  changeOccupantTiming2 (e) {
    if (this.isSendSmsInquiry(e.target.value)) {
      // emailと電話番号のtipsを表示
      const elements = document.getElementsByClassName(`${this.className}::sms-tips`)
      for (const element of elements) { element.style.display = 'block' }

      // emailの必須タグを非表示
      document.getElementsByClassName(`${this.className}::email-required`)[0].style.display = 'none'

      // emailのinputのrequiredを削除
      document.querySelector('input[name="inquiry[email]"]').removeAttribute('required')
    } else {
      // emailと電話番号のtipsを非表示
      const elements = document.getElementsByClassName(`${this.className}::sms-tips`)
      for (const element of elements) { element.style.display = 'none' }

      // emailの必須タグを表示
      document.getElementsByClassName(`${this.className}::email-required`)[0].style.display = 'inline-block'

      // emailのinputにrequiredを追加
      document.querySelector('input[name="inquiry[email]"]').setAttribute('required', '')
    }
  }

  isSendSmsInquiry (occupantTimingValue) {
    return [1, 2, 3, 4, 5].includes(parseInt(occupantTimingValue))
  }

  inputEmail (e) {
    if (e.target.value.slice(-1) === '@') {
      this.suggestTarget.style.display = 'block'
    }
  }

  clickSuggest (e) {
    this.emailTarget.value = this.emailTarget.value + e.target.dataset.value
    this.suggestTarget.style.display = 'none'
  }

  closeSuggest () {
    this.suggestTarget.style.display = 'none'
  }

  validateTel () {
    const value = this.telInputTarget.value
    if (value) {
      const phoneNumber = parsePhoneNumber(value, 'JP')
      if (phoneNumber?.isValid()) {
        this.telErrorTarget.style.display = 'none'

        if (this.emailTarget.value?.length > 0) {
          this.telNextTarget.disabled = false
        }

        // TODO SMSが送信できないことを伝える
        // smsが送信できる電話番号かどうかを判定し、結果をhidden_fieldにセットする
        document.getElementById('inquiry_is_sms_sendable_tel').value = this.isSmsSendableTel(value).toString()
      } else {
        this.telNextTarget.disabled = true
        this.telErrorTarget.style.display = 'flex'
      }
    }
  }

  isSmsSendableTel (rawPhoneNumber) {
    const regex = /^(070|080|090)/
    return regex.test(rawPhoneNumber)
  }

  get prefectureId () {
    return this.prefecturesTarget.value
  }

  get cityId () {
    return this.citiesTarget.value
  }

  get necessaryMedicalTreatmentIds () {
    const ret = []
    this.necessaryMedicalTreatmentsTargets.forEach(element => {
      if (element.checked) {
        ret.push(element.value)
      }
    })
    return ret
  }

  get assetTypesIds () {
    const ret = []
    this.assetTypesTargets.forEach(element => {
      if (element.checked) {
        ret.push(element.value)
      }
    })
    return ret
  }

  enableSubmit () {
    this.submitTargets.forEach(target => { target.disabled = false })
  }

  disableSubmit () {
    this.submitTargets.forEach(target => { target.disabled = true })
  }
}
